/**
 * 비회원 화면(질문 대화 `/ask` · 공개 랜딩 `/`)의 **로그인 관문** — 손짓마다 안내 문구, 로그인 화면 주소, 창이 미리 보일 답.
 * 순수 함수다 — 손잡이(`use-guest-hub-gate`)가 부른다.
 * 🔑 비회원은 서비스 화면을 하나도 열지 못한다 — 사이드바 메뉴(`menu`) · 답의 전체 보기(`answer`) · 카드(`open`)는 전부 로그인 안내다.
 *   안내 창은 그 안에서 바로 로그인한다(로그인 화면과 같은 몸통) · 껍데기의 「로그인」 링크는 로그인 화면(`guestLoginHref`)으로 간다.
 */

import type { AskBriefLine } from 'src/utils/ask/ask-answer-brief';
import type { AskAnswerOk, AskAnswerData } from 'src/types/ask/ask-ops';
import type { AskAnswerTile } from 'src/components/cmui/ask-answer/ask-answer-tiles';

import { paths } from 'src/routes/paths';

import { clipAtWord } from 'src/utils/text/clip-text';
import { withReturnTo } from 'src/utils/auth/return-to-storage';
import { subjectParticle } from 'src/utils/format/korean-particle';
import { type AskPreset, toAskPresetParam } from 'src/utils/ask/ask-preset';

/**
 * 비회원이 누를 수 있는 손짓.
 * `open` 카드·줄 열기 · `filter` 칩 고치기 · `menu` 사이드바·구역 문 · `answer` 답의 전체 보기·미리보기 · `followup` 이어 묻기 · `limit` 오늘 물을 수 있는 만큼 물었다.
 */
export type GuestAction = 'open' | 'filter' | 'menu' | 'answer' | 'followup' | 'limit';

/** 안내 창이 서는 자리 — 질문 대화(`ask` · 사이드바 포함) · 공개 랜딩(`home` — 「오늘의 뷰티 트렌드」) */
export type GuestGateSurface = 'ask' | 'home';

/** 안내 창의 본문 — 누른 손짓마다 한 줄 */
export const GUEST_GATE_MESSAGES: Record<GuestAction, string> = {
  open: '자세히 보기는 로그인하면 열려요.',
  filter: '기간·채널·국가 같은 조건은 로그인하면 바꿀 수 있어요.',
  menu: '이 메뉴는 로그인하면 바로 쓸 수 있어요.',
  answer: '가입하면 이 답의 전체 목록을 바로 볼 수 있어요.',
  followup: '가입하면 이어서 더 물어볼 수 있어요.',
  limit: '가입하면 계속 물어볼 수 있어요.',
};

/**
 * 홈 카드 · 구역이 말하는 것 — 창의 제목이 이것을 부른다(누른 자리 × 손짓으로 지은 질문의 주제).
 * `creator` 계정(앞 `@` 없이) · `brand` 브랜드 · `event` 「{브랜드} {이벤트 이름}」 · `mall` 판매몰 이름 · `list` 구역이 모아 보이는 목록(`GuestListTopic`).
 */
export type GuestCardTopic =
  | { kind: 'creator'; account: string }
  | { kind: 'brand'; brand: string }
  | { kind: 'event'; name: string }
  | { kind: 'mall'; mall: string }
  | { kind: 'list'; list: GuestListTopic };

/**
 * 구역이 모아 보이는 목록 — `posts` 요즘 크게 터진 글(바이럴 레퍼런스) · `creators` 반응 좋은 크리에이터 · `brands` 언급 많은 브랜드 · `ads` 새로 시작한 광고 ·
 * `events-month` 이번 달 이벤트 · `week-posts` 이번 주 반응 좋은 글 · `week-events` 이번 주 이벤트.
 */
export type GuestListTopic = 'posts' | 'creators' | 'brands' | 'ads' | 'events-month' | 'week-posts' | 'week-events';

/**
 * 창이 무엇을 두고 열렸나 — 있으면 머리가 그것을 이름으로 말하고 「가입하면 바로 무료로 볼 수 있어요」로 연다.
 * `card` 홈 카드 · 구역(로그인 뒤 그 질문에 답한다 · `preset` 이 있으면 창이 그 읽기의 실제 답을 미리 보인다) · `question` 비회원이 친 질문 · `section` 누른 구역 · 메뉴의 이름.
 */
export type GuestGateFocus =
  | { kind: 'card'; topic: GuestCardTopic; question: string; preset?: AskPreset }
  | { kind: 'question'; question: string }
  | { kind: 'section'; label: string };

/** 창의 글 한 벌 — `question` 이 있으면 말풍선 + `lines`(그 답에 나오는 것) · 없으면 `lines` 는 가입하면 열리는 것 */
export type GuestGateCopy = {
  /** 무료로 바로 보는 것을 말하는 창인가 — 아니면 「회원 전용 기능」 */
  free: boolean;
  eyebrow: string;
  title: string;
  /** 제목 아래 한 줄 — 무료로 바로 보는 창은 표제가 이미 말하므로 없다(`null`) */
  subtitle: string | null;
  question: string | null;
  lines: readonly string[];
  /** 미리 볼 읽기 — 홈 카드 · 구역의 창에만 있다(창이 그 답을 묻는다 · 못 받으면 `lines` 가 선다) */
  preset: AskPreset | null;
};

/**
 * 손짓 · 누른 것 → 창의 글. 누른 것이 없거나 이름이 비면 손짓마다의 한 줄(`GUEST_GATE_MESSAGES`)과 열리는 것 셋이다.
 * @param focus 누른 카드 · 친 질문 · 누른 구역
 */
export function guestGateCopy(action: GuestAction, focus?: GuestGateFocus | null): GuestGateCopy {
  if (focus?.kind === 'card') {
    const question = clean(focus.question);
    const title = cardTitle(focus.topic);
    if (question && title) {
      return { free: true, eyebrow: GUEST_FREE_EYEBROW, title, subtitle: null, question, lines: cardPreview(focus.topic), preset: focus.preset ?? null };
    }
  }
  if (focus?.kind === 'question') {
    const question = clean(focus.question);
    if (question) {
      const title = GUEST_QUESTION_TITLES[action](`‘${clipAtWord(question, TITLE_NAME_MAX)}’`);
      return { free: true, eyebrow: GUEST_FREE_EYEBROW, title, subtitle: null, question, lines: GUEST_QUESTION_PREVIEW, preset: null };
    }
  }
  if (focus?.kind === 'section') {
    const label = clean(focus.label);
    if (label) {
      return { free: true, eyebrow: GUEST_FREE_EYEBROW, title: `로그인하면 ${label}부터 바로 이어서 보여 드려요`, subtitle: null, question: null, lines: GUEST_GATE_BENEFITS, preset: null };
    }
  }
  return { free: false, eyebrow: GUEST_LOCKED_EYEBROW, title: GUEST_GATE_MESSAGES[action], subtitle: GUEST_BACK_SUBTITLE, question: null, lines: GUEST_GATE_BENEFITS, preset: null };
}

/** 부르는 자리가 덧씌우는 창 글(제목 · 한 줄 설명 · 열리는 것) — 주지 않은 칸은 그대로 */
export type GuestGateOverride = { title?: string; body?: string; benefits?: readonly string[] };

/**
 * 창의 글에 부르는 자리의 글을 덧씌운다.
 * 🔑 질문 말풍선이 선 창(누른 카드 · 친 질문)은 그 질문과 미리보기가 머리라 덧씌우지 않는다 — 질문이 없는 창(구역 · 기본 글)만 바뀐다.
 * @param override 없으면 `copy` 그대로
 */
export function withGuestGateOverride(copy: GuestGateCopy, override?: GuestGateOverride | null): GuestGateCopy {
  if (!override || copy.question) return copy;
  return {
    ...copy,
    title: override.title ?? copy.title,
    subtitle: override.body ?? copy.subtitle,
    lines: override.benefits ?? copy.lines,
  };
}

/**
 * 홈 카드의 로그인 뒤 돌아갈 곳 — 서비스 홈 대화에서 그 말을 묻고, 미리 정한 읽기로 바로 답한다(`/service?question=…&read=…`).
 * @param question 카드에서 지은 말
 * @param preset 미리 정한 읽기
 */
export function guestCardReturnPath(question: string, preset: AskPreset): string {
  return paths.service.findAsk(question.trim(), toAskPresetParam(preset));
}

/**
 * 질문 대화의 로그인 뒤 돌아갈 곳 — 서비스 홈 대화에서 같은 질문을 한 번 다시 묻는다(`/service?question=`).
 * @param question 비회원이 물은 말 — 비면 서비스 홈
 */
export function guestAskReturnPath(question: string): string {
  const q = question.trim();
  return q ? paths.service.findAsk(q) : paths.service.home;
}

/** 창의 미리보기 상태 — 받는 중 · 실물이 선 답 · 답에 나오는 것 목록으로(상한 · 실패 · 막힘 · 빈 답) */
/** 창의 답 미리보기 — `ready` 의 `lines` · `tiles` 가 있으면 답에서 짓는 글 줄(`askAnswerBriefLines`) · 섬네일 대신 그것을 세운다 */
export type GuestAskPreview = { status: 'loading' } | { status: 'ready'; data: AskAnswerOk; lines?: AskBriefLine[]; tiles?: AskAnswerTile[] } | { status: 'fallback' };

/**
 * 가입 안내 창이 미리 보일 답 — 수·실물이 선 답만. 요약은 그대로 둔다(창은 요약을 글 몇 줄로 적는다 · `askAnswerBriefLines`).
 * @returns 실패 · 막힘 · 빈 답이면 `null`(창은 답에 나오는 것 목록으로 선다)
 */
export function guestAnswerPreview(data: AskAnswerData | null | undefined): AskAnswerOk | null {
  if (!data || data.status !== 'ok') return null;
  switch (data.screen) {
    case 'contents':
      return data.lanes.ads.length + data.lanes.posts.length + data.lanes.products.length > 0 ? data : null;
    case 'brands':
      return data.overview || data.items.length > 0 ? data : null;
    default:
      return data.items.length > 0 ? data : null;
  }
}

/** 로그인 화면 주소 — 로그인 뒤 `returnPath` 로 돌아온다 */
export function guestLoginHref(returnPath: string): string {
  return withReturnTo(paths.auth.login, returnPath);
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 표제 — 누른 것이 있을 때 · 없을 때 */
const GUEST_FREE_EYEBROW = '가입하면 바로 무료로 볼 수 있어요';
const GUEST_LOCKED_EYEBROW = '회원 전용 기능';

/** 부제 — 누른 것이 없는 창에만 */
const GUEST_BACK_SUBTITLE = '가입은 무료예요. 로그인하면 지금 보던 화면으로 바로 돌아와요.';

/** 가입하면 열리는 것 — 비회원 껍데기에서 잠긴 것과 짝이다 */
const GUEST_GATE_BENEFITS = ['허브 전체 목록 · 검색 · 상세 보기', '기간 · 채널 · 국가 조건 바꾸기와 엑셀 내려받기', '우리 브랜드 · 경쟁사 추적과 알림'] as const;

/** 제목 속 이름의 글자 상한 — 넘으면 낱말 경계에서 「…」 */
const TITLE_NAME_MAX = 24;

/** 친 질문의 제목 — 손짓마다(`q` 는 따옴표를 두른 질문) */
const GUEST_QUESTION_TITLES: Record<GuestAction, (q: string) => string> = {
  open: (q) => `${q} 답을 자세히 바로 보여 드려요`,
  filter: (q) => `${q} 답의 기간·채널·국가를 바로 바꿔 볼 수 있어요`,
  menu: (q) => `${q} 답을 그 화면에서 바로 보여 드려요`,
  answer: (q) => `${q} 답의 전체 목록을 바로 보여 드려요`,
  followup: (q) => `${q}에 이어서 바로 더 물어볼 수 있어요`,
  limit: (q) => `${q} 질문도 바로 답해 드려요`,
};

/** 친 질문의 답에서 로그인하면 더 보는 것 */
const GUEST_QUESTION_PREVIEW = ['답의 전체 목록과 상세', '기간 · 채널 · 국가 조건 바꾸기', '이어서 더 물어보기'] as const;

/**
 * 홈 카드 · 구역의 답에 나오는 것 — 대화 화면이 그 읽기로 세우는 답 카드(find spec §1-a 「홈 답 카드」)에 있는 것만 적는다.
 * 창이 실제 답을 미리 보이지 못할 때(기다림 상한 · 실패 · 빈 답) 선다.
 */
const GUEST_CARD_PREVIEW = {
  creator: ['최근 30일 반응 좋은 글', '팔로워와 최근 30일 평균 조회', '팔로워에 비해 얼마나 반응을 받는지'],
  brand: ['채널별 언급 · 언급 순위 · 함께 언급되는 브랜드', '집행 중인 광고 · 내세우는 혜택', '판매몰 · 할인 · 최근 이벤트'],
  event: ['이벤트 유형 · 이름 · 기간', '함께 올라온 관련 글 수와 실제 이미지'],
  mall: ['판매몰 랭킹 순위와 상품', '가격 · 할인율 · 리뷰 수'],
} as const;

/** 구역 목록의 답에 나오는 것 */
const GUEST_LIST_PREVIEW: Record<GuestListTopic, readonly string[]> = {
  posts: ['팔로워 1만 이하인데 조회 10만 넘긴 글', '글마다 팔로워 · 조회 · 본문'],
  'week-posts': ['반응 큰 인기 글과 조회 · 좋아요', '글 수 · 조회 · 반응 · 글 쓴 계정 수', '채널 비중과 함께 언급된 성분 · 피부 고민'],
  creators: ['반응 좋은 크리에이터 순위', '팔로워와 최근 30일 평균 조회', '팔로워에 비해 얼마나 반응을 받는지'],
  brands: ['언급 많은 브랜드 순위', '언급 수 · 도달과 지난 기간 대비 변화'],
  ads: ['광고 소재와 집행 시작일', '최근 7일 새 소재 · 평균 집행 기간', '많이 내세운 혜택 · 노출 국가'],
  'events-month': ['브랜드 · 이벤트 유형 · 이름 · 기간', '함께 올라온 관련 글 수와 실제 이미지'],
  'week-events': ['브랜드 · 이벤트 유형 · 이름 · 기간', '함께 올라온 관련 글 수와 실제 이미지'],
};

/** 구역 목록의 제목 */
const GUEST_LIST_TITLES: Record<GuestListTopic, string> = {
  posts: '팔로워가 적어도 크게 터진 글, 여기서 바로 찾아요',
  'week-posts': '이번 주 반응 좋은 뷰티 글을 모아 보여 드려요',
  creators: '요즘 반응 좋은 뷰티 크리에이터를 모아 보여 드려요',
  brands: '요즘 언급 많은 뷰티 브랜드를 모아 보여 드려요',
  ads: '요즘 새로 나온 뷰티 광고를 모아 보여 드려요',
  'events-month': '이번 달 뷰티 이벤트를 모아 보여 드려요',
  'week-events': '이번 주 뷰티 이벤트를 모아 보여 드려요',
};

/** 카드가 말하는 것 → 제목 · 이름이 비면 빈 글자 */
function cardTitle(topic: GuestCardTopic): string {
  switch (topic.kind) {
    case 'creator': {
      const account = clipAtWord(clean(topic.account).replace(/^@+/, ''), TITLE_NAME_MAX);
      return account ? `@${account}${subjectParticle(account)} 요즘 어떤 글로 반응을 받는지 보여 드려요` : '';
    }
    case 'brand': {
      const brand = clipAtWord(clean(topic.brand), TITLE_NAME_MAX);
      return brand ? `${brand} 소셜 전략을 바로 보여 드려요` : '';
    }
    case 'event': {
      const name = clipAtWord(clean(topic.name), TITLE_NAME_MAX);
      return name ? `${name} 소식을 보여 드려요` : '';
    }
    case 'mall': {
      const mall = clipAtWord(clean(topic.mall), TITLE_NAME_MAX);
      return mall ? `${mall} 랭킹을 전부 보여 드려요` : '';
    }
    case 'list':
      return GUEST_LIST_TITLES[topic.list] ?? '';
    default:
      return '';
  }
}

function cardPreview(topic: GuestCardTopic): readonly string[] {
  return topic.kind === 'list' ? GUEST_LIST_PREVIEW[topic.list] : GUEST_CARD_PREVIEW[topic.kind];
}

function clean(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}
